import { Component, OnInit, Input, ViewEncapsulation, AfterContentInit, ViewChildren, QueryList } from '@angular/core';
import { ControlValueAccessor, FormGroup, FormBuilder, FormArray } from '@angular/forms';
import { NgbModal, NgbActiveModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs/Observable';

import { Field, FileField, MultiSelectField, MultiSelectListField } from '../../models';
import { FieldService } from '../../services/field.service';
import { ConditionService } from '../../services/condition.service';
import { MetadataService, ApiService } from '../../services';
import { BehaviorSubject } from 'rxjs';
import { FormulaService } from '../../services/formula.service';
import { FieldComponent } from '../field/field.component';

@Component({
    selector: 'sn-modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.css', '../beasley/beasley.component.css'],
    encapsulation: ViewEncapsulation.None
})
export class ModalComponent {
    @Input() view: string;
    @Input() data: any;    // this is the view item from the list view which may not contain all properties needed for this modal
    @ViewChildren(FieldComponent) field_components: QueryList<FieldComponent>;

    formFields: Array<Field<any>> = [];
    valueChangeFields: Array<Field<any>> = [];
    showOptions: boolean = false;
    form: FormGroup;
    viewSettings: any;
    viewFields: any;
    viewConditions: any;
    viewFormulas: any;
    viewButtons: any = [];
    viewImpOptions: any = [];
    viewItem: any;   // this is the view item retrieved specifically for this view

    mainTitle: string = "";
    modal_state: string = "modal_loading";
    conditionResults: any;
    calculatedResults: any;
    bSavingInProcess: boolean = false;
    currentEvent: string = "";

    private formValueOnChangeSubject = new BehaviorSubject<any>(null);
    public formValueOnChange = this.formValueOnChangeSubject.asObservable();

    public processListItemsBinding: Function;     // call back for sn-field on processing list items 
    public getErrorMessageBinding: Function;     // call back for condition error message

    constructor(
        private ngbModal: NgbModal,
        public activeModal: NgbActiveModal,
        private fieldService: FieldService,
        private apiService: ApiService,
        private metaDataService: MetadataService,
        private conditionService: ConditionService,
        private formulaService: FormulaService
    ) { }

    onFormChange() {
        var newFormFields = this.formFields;
        var newFormFields = this.fieldService.formValueToFormFields(this.form.value, newFormFields);
        this.calculate(newFormFields);
        this.validate(newFormFields);
        this.formFields = newFormFields;
        if (this.field_components) {
            // 2024-09-17: newly implemented to update field labels which could change depending on the values of the form item.  this can be expanded to support other needs
            this.field_components.forEach(f => {
                var updated_view_item = JSON.parse(JSON.stringify(this.viewItem));
                this.fieldService.updateDataWithFormValues(newFormFields, updated_view_item);
                f.onFormDataChanged(updated_view_item);
            });
        }
    }

    ngOnInit() {
        // must initialize an empty form as there is a delay on retrieving the view config form server
        this.form = this.fieldService.toFormGroup(this.formFields);
        this.processListItemsBinding = this.processListItems.bind(this);
        this.getErrorMessageBinding = this.getErrorMessage.bind(this);
        this.loadView(this.view, false);
        // console.log("usc-modal: ngOnInit");
    }

    loadView(view, bKeepFormValue: boolean) {
        // console.log("usc-modal: loadview");
        this.view = view;
        this.metaDataService.getFormViewMetaData(view, {}).subscribe(
            result => {
                let i = 0;
                this.viewFields = result.meta_data.view_fields;
                this.viewConditions = result.meta_data.view_conditions;
                this.viewFormulas = result.meta_data.view_formulas;
                this.viewSettings = result.meta_data.view_settings;
                if (this.viewSettings.titles && this.viewSettings.titles.length > 0) {
                    this.mainTitle = this.viewSettings.titles[0].label;
                }
                if (this.viewSettings.show_options) { this.showOptions = this.viewSettings.show_options; }
                if (this.data) {
                    this.loadViewItem(bKeepFormValue);
                }
            },
            err => { console.log(err) }
        )
    }

    loadViewItem(bKeepFormValue: boolean) {
        var view_ID = this.data.view_ID;
        this.apiService.getViewItem(this.view, view_ID).subscribe(
            result => {
                if (result.item) {
                    this.viewItem = result.item;
                    if (bKeepFormValue) {
                        // this will discard any server return values and keep all current form values
                        var newFormFields = this.fieldService.formValueToFormFields(this.form.value, this.formFields);
                        this.fieldService.updateDataWithFormValues(newFormFields, this.viewItem);
                    }
                    this.formFields = this.fieldService.getFormFields(this.viewFields, this.viewItem);
                    this.form = this.fieldService.toFormGroup(this.formFields);
                    // must evaluate all formula using the current form fields
                    this.calculate(this.formFields);
                    // must validate all form fields due to other control dependencies
                    this.validate(this.formFields);
                    // // event listner: subscribe to form value change event
                    this.form.valueChanges.subscribe(data => {
                        this.onFormChange();
                    });
                    this.modal_state = "modal_loaded";
                } else {
                    this.activeModal.close({ event: 'item_not_found', data: null });
                }
            },
            err => { console.log(err) }
        );
    }

    calculate(updateFormFields: Array<Field<any>>) {
        if (this.viewFormulas && updateFormFields) {
            this.calculatedResults = this.formulaService.calculateFormulaValues(this.viewFormulas, this.formFields, updateFormFields);
            updateFormFields.forEach(f => {
                // TODO: update form field values on fields that references a formula
            })
        }
    }

    validate(updateFormFields: Array<Field<any>>) {
        if (this.viewConditions && updateFormFields) {
            this.conditionResults = this.conditionService.evaluateFormValues(this.viewConditions, this.formFields, updateFormFields, this.calculatedResults);
            updateFormFields.forEach(f => {
                this.validateField(f);
            })
        }
    }

    validateField(field) {
        if (field.condition) {
            let ctrl = this.form.get(field.key);
            ctrl.setErrors(null);
            let condResult = this.conditionService.getCondition(this.conditionResults, field.condition);
            if (condResult && !condResult.result) {
                ctrl.setErrors(condResult.error);
                return condResult;
            }
        }
    }

    getConditionResult(condition) {
        let condResult = this.conditionService.getCondition(this.conditionResults, condition);
        return condResult;
    }

    isConditionValid(condition) {
        var condResult = this.getConditionResult(condition);
        // if condResult is null/undefined, it is valid, return true
        return condResult ? condResult.result : true;
    }

    onStep(event, btn_event) {
        this.onFormChange();
        var sn_current_step = this.form.controls["sn_current_step"].value;
        switch (btn_event) {
            case "prev_step":
                this.form.controls["sn_current_step"].setValue(sn_current_step - 1); break;
            case "next_step":
                // validate step fields before advancing to next step
                var form_field = this.formFields.find(f => f.key == "sn_current_step");
                var step_valid_condition = form_field.label + sn_current_step;
                if (this.isConditionValid(step_valid_condition)) this.form.controls["sn_current_step"].setValue(sn_current_step + 1);
                break;
        }
    }

    onSubmit(event, btn_event) {
        this.onFormChange();
        if (this.form.valid) {
            console.log(btn_event);
            var newFormFields = this.fieldService.formValueToFormFields(this.form.value, this.formFields);
            this.continueOnSubmit(event, btn_event, newFormFields);
        }
    }

    continueOnSubmit(event, btn_event, newFormFields) {
        this.bSavingInProcess = true;
        this.currentEvent = btn_event;
        if (btn_event == "  ") {
            this.activeModal.close({ event: btn_event, data: null });
        } else {
            var item_ID = this.viewItem.view_ID;
            var params = { event: btn_event };
            this.apiService.saveViewItem(this.view, item_ID, newFormFields, params).subscribe(
                result => {
                    this.activeModal.close({ event: btn_event, data: result });
                    this.bSavingInProcess = false;
                    this.currentEvent = "";
                }
            );
        }
    }

    onOptionsChange(e, o) {
        if (o.reload_view && o.value != this.view) {
            //console.log(o);
            this.loadView(o.value, true);
        }
    }

    isButtonVisible(btn) {
        var condResult = this.getConditionResult(btn.sn_visible);
        //console.log(condResult);
        return condResult ? condResult.result : true;
    }

    isFieldVisible(field) {
        var condResult = this.getConditionResult(field.sn_visible);
        //if (field.sn_visible == 'sa_url') console.log(condResult);
        //console.log(condResult);
        return condResult ? condResult.result : true;
    }

    processListItems(view, item, field: Field<any>, fm) {
        return this.fieldService.processListItems(view, item, field, fm.list_items, this.formFields);
    }

    getErrorMessage(field) {
        var condResult = this.getConditionResult(field.condition);
        var error_msg = "";
        if (condResult && condResult.result == false) {
            if (condResult.error_msg) {
                error_msg = condResult.error_msg;
            } else if (condResult.invalid_conditions && condResult.invalid_conditions.length > 0) {
                error_msg = this.findNonEmptyErrorMsg(condResult.invalid_conditions);
            } else if (condResult.unchanged_conditions && condResult.unchanged_conditions.length > 0) {
                error_msg = this.findNonEmptyErrorMsg(condResult.unchanged_conditions);
            }
        }
        return error_msg;
    }

    // triggered on emitted event (valueChange)
    // TODO: update form values based on the field type, not the view
    valueChange(event) {
        // console.log('modal valueChange', event);
        switch (event.field.display_type) {
            case FileField.control_type:
                if (event.file && event.file.ID) {
                    // set value to the control to retain this value
                    this.form.controls["fl_ID"].setValue(event.file.ID);
                }
                break;
            case MultiSelectField.control_type:
                if (event.field.value) {
                    // HACK: the form value didn't auto update when the field value was changed by the (change) event
                    // have to manually update the form.value
                    this.form.value[event.field.key] = event.field.value;
                }
                break;
            case MultiSelectListField.control_type:
                if (event.field.value) {
                    // HACK: the form value didn't auto update when the field value was changed by the (change) event
                    // have to manually update the form.value
                    this.form.value[event.field.key] = event.field.value;
                }
                break;
        }
        this.onFormChange();
    }

    dismiss() {
        if (!this.bSavingInProcess) this.activeModal.dismiss();
    }

    openUserLookup(): NgbModalRef {
        return null; // this.ngbModal.open(UscUserLookupComponent, { size: "lg", backdrop: 'static', keyboard: false });
    }

    findNonEmptyErrorMsg(invalid_conditions) {
        var error_msg = "";
        invalid_conditions.forEach(
            c => {
                if (error_msg.length == 0 && c.error_msg && c.error_msg.length > 0) {
                    error_msg = c.error_msg;
                }
            }
        )
        return error_msg;
    }

    getErrorMsg(field) {
        var condResult = this.validateField(field);
        var error_msg = "";
        if (condResult) {
            if (condResult.error_msg) {
                error_msg = condResult.error_msg;
            } else if (condResult.invalid_conditions && condResult.invalid_conditions.length > 0) {
                error_msg = this.findNonEmptyErrorMsg(condResult.invalid_conditions);
            } else if (condResult.unchanged_conditions && condResult.unchanged_conditions.length > 0) {
                error_msg = this.findNonEmptyErrorMsg(condResult.unchanged_conditions);
            }
        }
        return error_msg;
    }
}



// WEBPACK FOOTER //
// C:/Azure/Source/Workspaces/SalesNOW/SalesSOLO/WebSites/SalesSOLO.Web.Angular.telesales/src/app/components/modal/modal.component.ts